/* -----------------------------------------------------------------------
 * Nonogram Creator — Main Screen
 *
 * Production-grade puzzle designer with:
 * - Brush / Spray tools
 * - Pixel-art image import
 * - Optional image overlay (semi-transparent reference layer)
 * - Real-time solvability analysis (Web Worker)
 * - Multi grid splitting with per-region difficulty
 * - Export: share URL, JSON, PNG, print
 * ----------------------------------------------------------------------- */

import {
  useReducer,
  useCallback,
  useEffect,
  useRef,
  useMemo,
  useState,
} from 'react';
import type { ChangeEvent } from 'react';
import type { LevelData, PuzzleDefinition, Screen } from '../types';
import { ScreenLayout, SegmentedToggle } from './ui/Panel';
import { SettingsToggleRow } from './ui/SettingsMenu';
import { Button } from './ui/Button';
import { PillToggle, PillButton, PillSlider, PillStepper } from './ui/ToolbarPill';
import type { CreatorInit } from './CreatorSetup';
import { CreatorCanvas } from './CreatorCanvas';
import { Popup, PopupHeader, PopupBody, PopupFooter } from './Popup';
import { SharePopup } from './SharePopup';
import PixelIt from '../vendor/pixelit';
import { cluesFromGrid } from '@org/nonogram-engine';
import type {
  AnalyzeRequest,
  AnalyzeResponse,
  RegionAnalysis,
} from '../puzzles/analyze.worker';
import * as ShareService from '../shareService';
import './Creator.css';

/* ========================================================================= */
/* Types & Constants                                                          */
/* ========================================================================= */

type DrawTool = 'brush' | 'spray';
type RasterImageSource = HTMLImageElement | HTMLCanvasElement | ImageBitmap;
type OverlayImageSource = RasterImageSource;

const makeEmptyRegionAnalysis = (
  region: { row: number; col: number; width: number; height: number },
): RegionAnalysis => ({
  row: region.row,
  col: region.col,
  width: region.width,
  height: region.height,
  empty: true,
  filledCount: 0,
  density: 0,
  unique: null,
  solvable: null,
  sweepCount: null,
  requiresBacktracking: false,
  difficulty: 0,
  uniquenessHint: null,
  ambiguousCells: [],
  remainingUnknowns: 0,
});

interface Snapshot {
  grid: boolean[][];
  rows: number;
  cols: number;
  overlayLayers: OverlayLayer[];
}

interface OverlayPlacement {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface OverlayLayer {
  image: OverlayImageSource;
  placement: OverlayPlacement;
}

interface PendingImport {
  sourceImage: RasterImageSource;
  sourceKind: 'bitmap' | 'image';
  overlayImage: OverlayImageSource;
}

interface State {
  grid: boolean[][];
  rows: number;
  cols: number;
  name: string;
  history: Snapshot[];
  future: Snapshot[];
  paintStroke: 'idle' | 'pending' | 'recorded';
  activeTool: DrawTool;
  overlayLayers: OverlayLayer[];
  overlayOpacity: number;
  overlayVisible: boolean;
  overlayScale: number;
  overlayOffsetX: number;
  overlayOffsetY: number;
  regions: PuzzleDefinition[];
  selectedRegion: number | null;
}

type Action =
  | { type: 'SET_CELL'; row: number; col: number; value: boolean }
  | { type: 'PAINT_CELLS'; cells: [number, number][]; value: boolean }
  | { type: 'BEGIN_PAINT_STROKE' }
  | { type: 'END_PAINT_STROKE' }
  | { type: 'APPLY_IMAGE_IMPORT'; grid: boolean[][]; layer: OverlayLayer }
  | { type: 'UNDO' }
  | { type: 'REDO' }
  | { type: 'RESIZE'; rows: number; cols: number }
  | { type: 'CLEAR' }
  | { type: 'LOAD_LEVEL'; level: LevelData }
  | { type: 'SET_TOOL'; tool: DrawTool }
  | { type: 'SET_NAME'; name: string }
  | { type: 'SET_OVERLAY_OPACITY'; opacity: number }
  | { type: 'SET_OVERLAY_VISIBLE'; visible: boolean }
  | { type: 'SET_OVERLAY_SCALE'; scale: number }
  | { type: 'SET_OVERLAY_OFFSET'; offsetX: number; offsetY: number }
  | { type: 'RESET_OVERLAY_TRANSFORM' }
  | { type: 'SET_REGIONS'; regions: PuzzleDefinition[] }
  | { type: 'SET_SELECTED_REGION'; index: number | null }
  | { type: 'ADD_REGION'; region: PuzzleDefinition }
  | { type: 'DELETE_REGION'; index: number };

const MAX_HISTORY = 100;
const IMPORT_THRESHOLD = 160;
const DEFAULT_OVERLAY_OPACITY = 0.35;
const MIN_OVERLAY_SCALE = 0.25;
const MAX_OVERLAY_SCALE = 4;
const MAX_IMPORT_SOURCE_DIMENSION = 2048;

function makeEmptyGrid(rows: number, cols: number): boolean[][] {
  return Array.from({ length: rows }, () => Array(cols).fill(false));
}

function cloneGrid(grid: boolean[][]): boolean[][] {
  return grid.map((row) => [...row]);
}

function cloneOverlayLayers(layers: OverlayLayer[]): OverlayLayer[] {
  return layers.map((layer) => ({
    image: layer.image,
    placement: { ...layer.placement },
  }));
}

function resizeGrid(
  sourceGrid: boolean[][],
  sourceRows: number,
  sourceCols: number,
  targetRows: number,
  targetCols: number,
): boolean[][] {
  const newGrid = makeEmptyGrid(targetRows, targetCols);
  for (let r = 0; r < Math.min(sourceRows, targetRows); r++) {
    for (let c = 0; c < Math.min(sourceCols, targetCols); c++) {
      newGrid[r][c] = sourceGrid[r][c];
    }
  }
  return newGrid;
}

function pushHistory(state: State): { history: Snapshot[]; future: Snapshot[] } {
  const history: Snapshot[] = [
    ...state.history,
    {
      grid: cloneGrid(state.grid),
      rows: state.rows,
      cols: state.cols,
      overlayLayers: cloneOverlayLayers(state.overlayLayers),
    },
  ];
  if (history.length > MAX_HISTORY) history.shift();
  return { history, future: [] };
}

function mergeGridWithImport(baseGrid: boolean[][], importGrid: boolean[][]): boolean[][] {
  return baseGrid.map((row, rowIndex) =>
    row.map((cell, colIndex) => cell || Boolean(importGrid[rowIndex]?.[colIndex])),
  );
}

function invertGrid(grid: boolean[][]): boolean[][] {
  return grid.map((row) => row.map((cell) => !cell));
}

function gridFromLuminanceGrid(
  luminanceGrid: number[][],
  threshold: number,
  invert: boolean,
): boolean[][] {
  const grid = luminanceGrid.map((row) => row.map((value) => value < threshold));
  return invert ? invertGrid(grid) : grid;
}

function getSprayAreaCells(
  centerRow: number,
  centerCol: number,
  rows: number,
  cols: number,
): [number, number][] {
  const cells: [number, number][] = [];
  for (let r = Math.max(0, centerRow - 1); r <= Math.min(rows - 1, centerRow + 1); r++) {
    for (let c = Math.max(0, centerCol - 1); c <= Math.min(cols - 1, centerCol + 1); c++) {
      cells.push([r, c]);
    }
  }
  return cells;
}

function getSprayCells(
  centerRow: number,
  centerCol: number,
  rows: number,
  cols: number,
  grid?: boolean[][],
): [number, number][] {
  const areaCells = getSprayAreaCells(centerRow, centerCol, rows, cols);
  const unsprayedCells = grid
    ? areaCells.filter(([r, c]) => !grid[r]?.[c])
    : areaCells;
  const sourceCells = unsprayedCells.length > 0 ? unsprayedCells : areaCells;
  const roll = Math.random();
  const targetCount =
    roll < 0.12
      ? 0
      : sourceCells.length > 1 && roll < 0.18
        ? 2
        : 1;
  const remaining = [...sourceCells];
  const sprayedCells: [number, number][] = [];

  while (remaining.length > 0 && sprayedCells.length < targetCount) {
    const index = Math.floor(Math.random() * remaining.length);
    sprayedCells.push(remaining[index]);
    remaining.splice(index, 1);
  }

  return sprayedCells;
}

const DEFAULT_ROWS = 15;
const DEFAULT_COLS = 15;

function makeFullGridRegion(rows: number, cols: number): PuzzleDefinition {
  return { id: '0_0', row: 0, col: 0, width: cols, height: rows, difficulty: 0 };
}

function createInitialState(init?: CreatorInit | null): State {
  const base: State = {
    grid: makeEmptyGrid(DEFAULT_ROWS, DEFAULT_COLS),
    rows: DEFAULT_ROWS,
    cols: DEFAULT_COLS,
    name: '',
    history: [],
    future: [],
    paintStroke: 'idle',
    activeTool: 'brush',
    overlayLayers: [],
    overlayOpacity: DEFAULT_OVERLAY_OPACITY,
    overlayVisible: true,
    overlayScale: 1,
    overlayOffsetX: 0,
    overlayOffsetY: 0,
    regions: [makeFullGridRegion(DEFAULT_ROWS, DEFAULT_COLS)],
    selectedRegion: null,
  };

  if (!init) return base;

  if (init.kind === 'new') {
    return {
      ...base,
      rows: init.size,
      cols: init.size,
      grid: makeEmptyGrid(init.size, init.size),
      regions: [makeFullGridRegion(init.size, init.size)],
    };
  }

  // Remix: load an existing level as the starting point
  const level = init.level;
  const newRows = level.solution.length;
  const newCols = level.solution[0]?.length ?? 0;
  const regions = level.puzzles.length > 0
    ? level.puzzles.map((p) => ({ ...p }))
    : [makeFullGridRegion(newRows, newCols)];

  return {
    ...base,
    rows: newRows,
    cols: newCols,
    grid: level.solution.map((row) => [...row]),
    name: level.name,
    regions,
  };
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'SET_CELL': {
      if (
        action.row < 0 || action.row >= state.rows ||
        action.col < 0 || action.col >= state.cols ||
        state.grid[action.row][action.col] === action.value
      ) {
        return state;
      }
      const { history, future } = pushHistory(state);
      const grid = cloneGrid(state.grid);
      grid[action.row][action.col] = action.value;
      return { ...state, grid, history, future };
    }
    case 'PAINT_CELLS': {
      const changedCells = action.cells.filter(
        ([r, c]) =>
          r >= 0 &&
          r < state.rows &&
          c >= 0 &&
          c < state.cols &&
          state.grid[r][c] !== action.value,
      );
      if (changedCells.length === 0) return state;

      let history = state.history;
      let future = state.future;
      let paintStroke = state.paintStroke;

      if (paintStroke === 'pending') {
        ({ history, future } = pushHistory(state));
        paintStroke = 'recorded';
      } else if (paintStroke === 'idle') {
        ({ history, future } = pushHistory(state));
      }

      const grid = cloneGrid(state.grid);
      for (const [r, c] of changedCells) {
        grid[r][c] = action.value;
      }
      return { ...state, grid, history, future, paintStroke };
    }
    case 'BEGIN_PAINT_STROKE':
      return state.paintStroke === 'idle'
        ? { ...state, paintStroke: 'pending' }
        : state;
    case 'END_PAINT_STROKE':
      return state.paintStroke === 'idle'
        ? state
        : { ...state, paintStroke: 'idle' };
    case 'APPLY_IMAGE_IMPORT': {
      const { history, future } = pushHistory(state);
      return {
        ...state,
        grid: mergeGridWithImport(state.grid, action.grid),
        overlayLayers: [...state.overlayLayers, { image: action.layer.image, placement: { ...action.layer.placement } }],
        paintStroke: 'idle',
        history,
        future,
      };
    }
    case 'UNDO': {
      if (state.history.length === 0) return state;
      const history = [...state.history];
      const prev = history.pop()!;
      const current: Snapshot = {
        grid: cloneGrid(state.grid),
        rows: state.rows,
        cols: state.cols,
        overlayLayers: cloneOverlayLayers(state.overlayLayers),
      };
      return {
        ...state,
        grid: prev.grid,
        rows: prev.rows,
        cols: prev.cols,
        paintStroke: 'idle',
        overlayLayers: cloneOverlayLayers(prev.overlayLayers),
        regions: [makeFullGridRegion(prev.rows, prev.cols)],
        history,
        future: [current, ...state.future],
      };
    }
    case 'REDO': {
      if (state.future.length === 0) return state;
      const future = [...state.future];
      const next = future.shift()!;
      const current: Snapshot = {
        grid: cloneGrid(state.grid),
        rows: state.rows,
        cols: state.cols,
        overlayLayers: cloneOverlayLayers(state.overlayLayers),
      };
      return {
        ...state,
        grid: next.grid,
        rows: next.rows,
        cols: next.cols,
        paintStroke: 'idle',
        overlayLayers: cloneOverlayLayers(next.overlayLayers),
        regions: [makeFullGridRegion(next.rows, next.cols)],
        history: [...state.history, current],
        future,
      };
    }
    case 'RESIZE': {
      const { history, future } = pushHistory(state);
      const newGrid = resizeGrid(
        state.grid,
        state.rows,
        state.cols,
        action.rows,
        action.cols,
      );
      return {
        ...state,
        rows: action.rows,
        cols: action.cols,
        grid: newGrid,
        paintStroke: 'idle',
        regions: [makeFullGridRegion(action.rows, action.cols)],
        history,
        future,
      };
    }
    case 'CLEAR': {
      const { history, future } = pushHistory(state);
      return {
        ...state,
        grid: makeEmptyGrid(state.rows, state.cols),
        paintStroke: 'idle',
        overlayLayers: [],
        overlayScale: 1,
        overlayOffsetX: 0,
        overlayOffsetY: 0,
        history,
        future,
      };
    }
    case 'LOAD_LEVEL': {
      const { history, future } = pushHistory(state);
      const newRows = action.level.solution.length;
      const newCols = action.level.solution[0]?.length ?? 0;
      const regions = action.level.puzzles.length > 0
        ? action.level.puzzles.map((p) => ({ ...p }))
        : [makeFullGridRegion(newRows, newCols)];
      return {
        ...state,
        grid: action.level.solution.map((row) => [...row]),
        rows: newRows,
        cols: newCols,
        name: action.level.name,
        regions,
        selectedRegion: null,
        paintStroke: 'idle',
        overlayLayers: [],
        overlayScale: 1,
        overlayOffsetX: 0,
        overlayOffsetY: 0,
        history,
        future,
      };
    }
    case 'SET_TOOL':
      return { ...state, activeTool: action.tool, paintStroke: 'idle' };
    case 'SET_NAME':
      return { ...state, name: action.name };
    case 'SET_OVERLAY_OPACITY':
      return { ...state, overlayOpacity: action.opacity };
    case 'SET_OVERLAY_VISIBLE':
      return { ...state, overlayVisible: action.visible };
    case 'SET_OVERLAY_SCALE':
      return {
        ...state,
        overlayScale: Math.max(MIN_OVERLAY_SCALE, Math.min(MAX_OVERLAY_SCALE, action.scale)),
      };
    case 'SET_OVERLAY_OFFSET':
      return { ...state, overlayOffsetX: action.offsetX, overlayOffsetY: action.offsetY };
    case 'RESET_OVERLAY_TRANSFORM':
      return { ...state, overlayScale: 1, overlayOffsetX: 0, overlayOffsetY: 0 };
    case 'SET_REGIONS':
      return { ...state, regions: action.regions, selectedRegion: null };
    case 'SET_SELECTED_REGION':
      return { ...state, selectedRegion: action.index };
    case 'ADD_REGION':
      return { ...state, regions: [...state.regions, action.region].sort((a, b) => a.row - b.row || a.col - b.col) };
    case 'DELETE_REGION': {
      const regions = state.regions.filter((_, i) => i !== action.index);
      return { ...state, regions, selectedRegion: null };
    }
    default:
      return state;
  }
}

/** AABB overlap check (excludeIndex to skip the region being resized). */
function regionsOverlap(
  regions: { row: number; col: number; width: number; height: number }[],
  bounds: { row: number; col: number; width: number; height: number },
  excludeIndex?: number,
): boolean {
  for (let i = 0; i < regions.length; i++) {
    if (i === excludeIndex) continue;
    const r = regions[i];
    if (
      bounds.col < r.col + r.width &&
      bounds.col + bounds.width > r.col &&
      bounds.row < r.row + r.height &&
      bounds.row + bounds.height > r.row
    ) {
      return true;
    }
  }
  return false;
}

function getImportScale(imageWidth: number, imageHeight: number, rows: number, cols: number): number {
  const widthScale = (cols / Math.max(1, imageWidth)) * 100;
  const heightScale = (rows / Math.max(1, imageHeight)) * 100;
  return Math.max(1, Math.min(50, Math.round(Math.min(widthScale, heightScale))));
}

function getSourceDimensions(source: RasterImageSource): { width: number; height: number } {
  if (source instanceof HTMLImageElement) {
    return {
      width: Math.max(1, source.naturalWidth || source.width),
      height: Math.max(1, source.naturalHeight || source.height),
    };
  }

  return {
    width: Math.max(1, source.width),
    height: Math.max(1, source.height),
  };
}

function canvasFromSource(source: RasterImageSource, maxDimension?: number): HTMLCanvasElement {
  const { width: sourceWidth, height: sourceHeight } = getSourceDimensions(source);
  const scale = maxDimension
    ? Math.min(1, maxDimension / Math.max(sourceWidth, sourceHeight))
    : 1;
  const width = Math.max(1, Math.round(sourceWidth * scale));
  const height = Math.max(1, Math.round(sourceHeight * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;
  ctx.imageSmoothingEnabled = scale < 1;
  ctx.clearRect(0, 0, width, height);
  ctx.drawImage(source, 0, 0, width, height);
  return canvas;
}

function detectBitmapImport(source: RasterImageSource): boolean {
  const canvas = canvasFromSource(source, 128);
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return false;

  const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const pixelCount = data.length / 4;
  if (pixelCount === 0) return false;

  let binaryAlphaPixels = 0;
  let opaquePixels = 0;
  let grayscalePixels = 0;
  let extremePixels = 0;

  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const a = data[i + 3];
    if (a <= 12 || a >= 243) {
      binaryAlphaPixels++;
    }
    if (a <= 12) continue;

    opaquePixels++;
    const maxChannel = Math.max(r, g, b);
    const minChannel = Math.min(r, g, b);
    if (maxChannel - minChannel <= 18) {
      grayscalePixels++;
    }

    const luminance = r * 0.2126 + g * 0.7152 + b * 0.0722;
    if (luminance <= 40 || luminance >= 215) {
      extremePixels++;
    }
  }

  if (opaquePixels === 0) return false;

  const alphaBinaryRatio = binaryAlphaPixels / pixelCount;
  const grayscaleRatio = grayscalePixels / opaquePixels;
  const extremeRatio = extremePixels / opaquePixels;

  return alphaBinaryRatio >= 0.9 && grayscaleRatio >= 0.96 && extremeRatio >= 0.94;
}

function getOverlayPlacement(
  source: RasterImageSource,
  rows: number,
  cols: number,
  overlayScale: number,
  overlayOffsetX: number,
  overlayOffsetY: number,
): OverlayPlacement {
  const { width: sourceWidth, height: sourceHeight } = getSourceDimensions(source);
  const baseScale = Math.min(
    cols / Math.max(1, sourceWidth),
    rows / Math.max(1, sourceHeight),
  );
  const width = sourceWidth * baseScale * overlayScale;
  const height = sourceHeight * baseScale * overlayScale;
  return {
    x: (cols - width) / 2 + overlayOffsetX * cols,
    y: (rows - height) / 2 + overlayOffsetY * rows,
    width,
    height,
  };
}

function sampleCanvasToLuminanceGridWithTransform(
  sourceCanvas: HTMLCanvasElement,
  rows: number,
  cols: number,
  placement: OverlayPlacement,
): number[][] {
  const sampleCanvas = document.createElement('canvas');
  sampleCanvas.width = cols;
  sampleCanvas.height = rows;
  const ctx = sampleCanvas.getContext('2d');
  if (!ctx) return Array.from({ length: rows }, () => Array(cols).fill(255));

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, cols, rows);
  ctx.imageSmoothingEnabled = false;

  ctx.drawImage(
    sourceCanvas,
    0,
    0,
    sourceCanvas.width,
    sourceCanvas.height,
    placement.x,
    placement.y,
    placement.width,
    placement.height,
  );

  const { data } = ctx.getImageData(0, 0, cols, rows);
  return Array.from({ length: rows }, (_, rowIndex) =>
    Array.from({ length: cols }, (_, colIndex) => {
      const offset = (rowIndex * cols + colIndex) * 4;
      const alpha = data[offset + 3] / 255;
      const luminance =
        data[offset] * 0.2126 +
        data[offset + 1] * 0.7152 +
        data[offset + 2] * 0.0722;
      return alpha > 0.1 ? luminance : 255;
    }),
  );
}

function directImageToLuminanceGrid(
  source: RasterImageSource,
  rows: number,
  cols: number,
  placement: OverlayPlacement,
): number[][] {
  const sourceCanvas = canvasFromSource(source);
  return sampleCanvasToLuminanceGridWithTransform(
    sourceCanvas,
    rows,
    cols,
    placement,
  );
}

function pixelateImageToLuminanceGrid(
  source: RasterImageSource,
  rows: number,
  cols: number,
  pixelScale: number,
  placement: OverlayPlacement,
): number[][] {
  const sourceCanvas = canvasFromSource(source);
  const canvas = document.createElement('canvas');
  const pixelIt = new PixelIt({
    from: sourceCanvas,
    to: canvas,
    scale: pixelScale,
  });

  pixelIt.pixelate();
  pixelIt.convertGrayscale();

  return sampleCanvasToLuminanceGridWithTransform(
    canvas,
    rows,
    cols,
    placement,
  );
}

async function loadImageElement(blob: Blob): Promise<HTMLImageElement> {
  const objectUrl = URL.createObjectURL(blob);

  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Could not load image'));
    };
    image.decoding = 'async';
    image.src = objectUrl;
  });
}

function isHeicLikeFile(file: File): boolean {
  const lowerName = file.name.toLowerCase();
  const lowerType = file.type.toLowerCase();
  return (
    lowerName.endsWith('.heic') ||
    lowerName.endsWith('.heif') ||
    lowerType.includes('heic') ||
    lowerType.includes('heif')
  );
}

async function convertHeicBlob(file: File): Promise<Blob> {
  const { heicTo } = await import('heic-to/csp');
  const converted = await heicTo({
    blob: file,
    type: 'image/png',
    quality: 0.92,
  });
  const firstBlob = Array.isArray(converted) ? converted[0] : converted;
  if (!(firstBlob instanceof Blob)) {
    throw new Error('HEIC conversion did not return an image blob.');
  }
  return firstBlob;
}

async function loadImageSource(file: File): Promise<RasterImageSource> {
  const sourceBlob = isHeicLikeFile(file)
    ? await convertHeicBlob(file)
    : file;

  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(sourceBlob);
    } catch {}
  }

  try {
    return await loadImageElement(sourceBlob);
  } catch (error) {
    if (isHeicLikeFile(file)) {
      throw new Error('This browser could not display the converted HEIC image.');
    }
    throw error;
  }
}

async function prepareImageImport(file: File): Promise<PendingImport> {
  const decodedSource = await loadImageSource(file);
  const sourceImage = canvasFromSource(decodedSource, MAX_IMPORT_SOURCE_DIMENSION);
  if (typeof ImageBitmap !== 'undefined' && decodedSource instanceof ImageBitmap) {
    decodedSource.close();
  }

  const sourceKind = detectBitmapImport(sourceImage) ? 'bitmap' : 'image';

  let overlayImage: OverlayImageSource = sourceImage;
  if (typeof createImageBitmap === 'function') {
    try {
      overlayImage = await createImageBitmap(sourceImage);
    } catch {}
  }

  return {
    sourceImage,
    sourceKind,
    overlayImage,
  };
}

/* ========================================================================= */
/* Creator Component                                                          */
/* ========================================================================= */

interface CreatorProps {
  onNavigate: (screen: Screen) => void;
  initialConfig?: CreatorInit | null;
}

export function Creator({ onNavigate, initialConfig }: CreatorProps) {
  const [state, dispatch] = useReducer(reducer, initialConfig ?? null, createInitialState);
  const {
    grid, rows, cols, name, activeTool,
    overlayLayers, overlayOpacity, overlayScale, overlayOffsetX, overlayOffsetY,
    regions, selectedRegion,
  } = state;

  // Span hints are always on. Clues are an optional overlay (default off).
  const [showClues, setShowClues] = useState(false);
  const showSpanHints = true;

  // Multi-puzzle mode: when off, Arrange tab is disabled and the level is
  // treated as a single full-grid puzzle. Seeded from setup config.
  const [multiPuzzleEnabled, setMultiPuzzleEnabled] = useState(
    initialConfig?.multi ?? false,
  );

  // Board width — mirrors CreatorCanvas logic so toolbars match the canvas
  const boardClues = useMemo(() => cluesFromGrid(grid), [grid]);
  const clueRowLen = Math.max(1, ...boardClues.rows.map((c: number[]) => c.length));
  const clueColLen = Math.max(1, ...boardClues.cols.map((c: number[]) => c.length));
  const maxRowClueLen = (showClues ? clueRowLen : 0) + (showSpanHints ? 1 : 0);
  const maxColClueLen = (showClues ? clueColLen : 0) + (showSpanHints ? 1 : 0);
  const totalCols = maxRowClueLen + cols;
  const totalRows = maxColClueLen + rows;

  // Track viewport for responsive board sizing.
  const [viewport, setViewport] = useState(() => ({
    w: typeof window !== 'undefined' ? window.innerWidth : 1200,
    h: typeof window !== 'undefined' ? window.innerHeight : 800,
  }));
  useEffect(() => {
    const handler = () => setViewport({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // Available space for the canvas (not the whole board area). Reserve vertical for:
  //   top bar (72) + title (~53) + top toolbar (~30) + bottom toolbar (~72) + view toggles (~60) + paddings/gaps (~50)
  const RESERVED_VERTICAL = 340;
  const RESERVED_HORIZONTAL = 80;
  const availH = Math.max(260, viewport.h - RESERVED_VERTICAL);
  const availW = Math.max(300, viewport.w - RESERVED_HORIZONTAL);
  const widthFromHeight = (availH * totalCols) / Math.max(1, totalRows);
  const boardW = Math.round(
    Math.min(1000, Math.max(460, Math.min(widthFromHeight, availW))),
  );

  // Mode: size, arrange (regions), draw, or image reference
  const [mode, setMode] = useState<'size' | 'draw' | 'regions' | 'image'>('draw');
  const modeRef = useRef(mode);
  modeRef.current = mode;

  // Paint mode ref (for brush drag behavior)
  const paintModeRef = useRef<boolean | null>(null);
  const lastSprayCenterRef = useRef<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [pendingImport, setPendingImport] = useState<PendingImport | null>(null);
  const [importThreshold, setImportThreshold] = useState(IMPORT_THRESHOLD);
  const [importInvert, setImportInvert] = useState(false);
  const [showOverlayImage, setShowOverlayImage] = useState(true);

  // Info popups
  const [showUniquenessPopup, setShowUniquenessPopup] = useState(false);
  const [showDifficultyPopup, setShowDifficultyPopup] = useState(false);
  const [showSharePopup, setShowSharePopup] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  // Hover position for previews
  const [hoverCell, setHoverCell] = useState<{ row: number; col: number } | null>(null);

  // Inline title rename
  const [isRenamingTitle, setIsRenamingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const titleInputRef = useRef<HTMLInputElement>(null);

  const displayedTitle = name.trim() || 'Custom level';

  const startRenamingTitle = useCallback(() => {
    setTitleDraft(name);
    setIsRenamingTitle(true);
  }, [name]);

  const commitRenamingTitle = useCallback(() => {
    const next = titleDraft.trim();
    dispatch({ type: 'SET_NAME', name: next });
    setIsRenamingTitle(false);
  }, [titleDraft]);

  const cancelRenamingTitle = useCallback(() => {
    setIsRenamingTitle(false);
  }, []);

  useEffect(() => {
    if (isRenamingTitle) titleInputRef.current?.select();
  }, [isRenamingTitle]);

  /* ---------- Solvability Analysis (web worker, debounced) ---------- */

  // The worker always analyzes at least one region. When the user hasn't
  // drawn any, we synthesize a full-grid region so ambiguous cells and
  // uniqueness hints still drive the UI in "simple mode". `regionAnalysis`
  // is indexed against `effectiveRegions`, not the raw `regions` array.
  const effectiveRegions = useMemo<PuzzleDefinition[]>(
    () =>
      regions.length > 0 ? regions : [makeFullGridRegion(rows, cols)],
    [regions, rows, cols],
  );

  const [regionAnalysis, setRegionAnalysis] = useState<RegionAnalysis[]>([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [makeUniqueWorking, setMakeUniqueWorking] = useState(false);
  const [removeBtWorking, setRemoveBtWorking] = useState(false);

  const workerRef = useRef<Worker | null>(null);
  const editorIdRef = useRef(0);
  const makeUniqueIdRef = useRef(0);
  const removeBtIdRef = useRef(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const w = new Worker(
      new URL('../puzzles/analyze.worker.ts', import.meta.url),
      { type: 'module' },
    );
    workerRef.current = w;

    const handler = (e: MessageEvent<AnalyzeResponse>) => {
      const msg = e.data;
      if (msg.kind === 'editor') {
        if (msg.id !== editorIdRef.current) return;
        setRegionAnalysis(msg.regions);
        setAnalyzing(false);
      } else if (msg.kind === 'makeUnique') {
        if (msg.id !== makeUniqueIdRef.current) return;
        setMakeUniqueWorking(false);
        if (msg.cellsToFill.length > 0 || msg.cellsToClear.length > 0) {
          dispatch({ type: 'BEGIN_PAINT_STROKE' });
          if (msg.cellsToFill.length > 0) {
            dispatch({
              type: 'PAINT_CELLS',
              cells: msg.cellsToFill,
              value: true,
            });
          }
          if (msg.cellsToClear.length > 0) {
            dispatch({
              type: 'PAINT_CELLS',
              cells: msg.cellsToClear,
              value: false,
            });
          }
          dispatch({ type: 'END_PAINT_STROKE' });
        }
      } else if (msg.kind === 'removeBacktracking') {
        if (msg.id !== removeBtIdRef.current) return;
        setRemoveBtWorking(false);
        if (msg.cellsToFill.length > 0) {
          dispatch({ type: 'BEGIN_PAINT_STROKE' });
          dispatch({
            type: 'PAINT_CELLS',
            cells: msg.cellsToFill,
            value: true,
          });
          dispatch({ type: 'END_PAINT_STROKE' });
        }
      }
    };

    w.addEventListener('message', handler);
    return () => {
      w.removeEventListener('message', handler);
      w.terminate();
      workerRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const filled = grid.some((row) => row.some(Boolean));
    if (!filled) {
      // Invalidate any in-flight request so a late response can't clobber state.
      editorIdRef.current++;
      setRegionAnalysis(effectiveRegions.map(makeEmptyRegionAnalysis));
      setAnalyzing(false);
      return;
    }
    const id = ++editorIdRef.current;
    const snapshotGrid = grid;
    const snapshotRegions = effectiveRegions;
    setAnalyzing(true);
    debounceRef.current = setTimeout(() => {
      const w = workerRef.current;
      if (!w || id !== editorIdRef.current) return;
      const req: AnalyzeRequest = {
        kind: 'editor',
        id,
        grid: snapshotGrid,
        regions: snapshotRegions,
      };
      w.postMessage(req);
    }, 150);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [grid, effectiveRegions]);

  /* ---------- Keyboard shortcuts ---------- */

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const meta = e.metaKey || e.ctrlKey;
      if (meta && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        dispatch({ type: 'UNDO' });
      } else if (meta && e.key === 'z' && e.shiftKey) {
        e.preventDefault();
        dispatch({ type: 'REDO' });
      } else if (meta && e.key === 'y') {
        e.preventDefault();
        dispatch({ type: 'REDO' });
      } else if (e.key === 'Escape') {
        dispatch({ type: 'SET_SELECTED_REGION', index: null });
      } else if (!meta && !e.altKey && modeRef.current === 'draw') {
        switch (e.key) {
          case 'b':
          case 'p': dispatch({ type: 'SET_TOOL', tool: 'brush' }); break;
          case 's': dispatch({ type: 'SET_TOOL', tool: 'spray' }); break;
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  /* ---------- Tool previews ---------- */

  const previewCells = useMemo<Set<string>>(() => {
    if (!hoverCell) return new Set();
    const { row, col } = hoverCell;

    if (activeTool === 'brush') {
      return new Set([`${row},${col}`]);
    }

    if (activeTool === 'spray') {
      const cells = getSprayAreaCells(row, col, rows, cols);
      return new Set(cells.map(([r, c]) => `${r},${c}`));
    }

    return new Set();
  }, [hoverCell, activeTool, rows, cols]);

  const previewMode =
    activeTool === 'brush'
      ? 'brush'
      : activeTool === 'spray'
        ? 'spray'
        : 'none' as const;

  /* ---------- Canvas interaction handlers (draw mode) ---------- */

  const handleCellDown = useCallback(
    (row: number, col: number) => {
      setHoverCell({ row, col });
      dispatch({ type: 'BEGIN_PAINT_STROKE' });
      switch (activeTool) {
        case 'brush': {
          lastSprayCenterRef.current = null;
          const newVal = !grid[row]?.[col];
          paintModeRef.current = newVal;
          dispatch({ type: 'PAINT_CELLS', cells: [[row, col]], value: newVal });
          break;
        }
        case 'spray': {
          lastSprayCenterRef.current = `${row},${col}`;
          paintModeRef.current = true;
          dispatch({
            type: 'PAINT_CELLS',
            cells: getSprayCells(row, col, rows, cols, grid),
            value: true,
          });
          break;
        }
      }
    },
    [activeTool, grid, rows, cols],
  );

  const handleCellMove = useCallback(
    (row: number, col: number) => {
      setHoverCell({ row, col });
      if (paintModeRef.current !== null && activeTool === 'spray') {
        const sprayKey = `${row},${col}`;
        if (lastSprayCenterRef.current === sprayKey) {
          return;
        }
        lastSprayCenterRef.current = sprayKey;
        dispatch({
          type: 'PAINT_CELLS',
          cells: getSprayCells(row, col, rows, cols, grid),
          value: true,
        });
        return;
      }
      if (paintModeRef.current !== null) {
        const needsUpdate = grid[row]?.[col] !== paintModeRef.current;
        if (needsUpdate) {
          dispatch({ type: 'PAINT_CELLS', cells: [[row, col]], value: paintModeRef.current });
        }
      }
    },
    [activeTool, grid, rows, cols],
  );

  const handleCellUp = useCallback(() => {
    paintModeRef.current = null;
    lastSprayCenterRef.current = null;
    dispatch({ type: 'END_PAINT_STROKE' });
  }, []);

  const handleCellLeave = useCallback(() => {
    setHoverCell(null);
    lastSprayCenterRef.current = null;
  }, []);

  const handleClearBoard = useCallback(() => {
    setPendingImport(null);
    setImportThreshold(IMPORT_THRESHOLD);
    setImportInvert(false);
    dispatch({ type: 'CLEAR' });
  }, []);

  /* ---------- Export / Share actions ---------- */

  /* ---------- Auto-save to localStorage ---------- */

  // Stable key for this editing session. Remixed levels reuse their storage
  // key so subsequent edits update the same entry instead of creating dupes.
  const storageKey = useMemo(() => {
    if (
      initialConfig?.kind === 'remix' &&
      initialConfig.level.id?.startsWith('nonogram_creator_')
    ) {
      return initialConfig.level.id;
    }
    return `nonogram_creator_${Date.now()}`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep latest save action in a ref so the debounced effect always uses
  // the most recent data without re-subscribing on every keystroke.
  const saveRef = useRef<() => void>(() => {});
  saveRef.current = () => {
    const hasContent = grid.some((row) => row.some(Boolean));
    try {
      if (hasContent) {
        localStorage.setItem(storageKey, JSON.stringify(buildExportData()));
      } else {
        localStorage.removeItem(storageKey);
      }
    } catch { /* storage full — silently drop */ }
  };

  useEffect(() => {
    const id = setTimeout(() => saveRef.current(), 500);
    return () => clearTimeout(id);
  }, [grid, name, rows, cols, regions]);

  const handleShare = useCallback(() => {
    setShowSharePopup(true);
  }, []);

  const handlePlay = useCallback(async () => {
    try {
      const shareUrl = await ShareService.url({ solution: grid });
      const search = new URL(shareUrl).search;
      onNavigate({ type: 'play', search });
    } catch { /* noop */ }
  }, [grid, onNavigate]);

  const handleImportImage = useCallback(async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsImporting(true);
    try {
      const nextImport = await prepareImageImport(file);
      setPendingImport(nextImport);
      console.info('Nonogram import mode', {
        fileName: file.name,
        sourceKind: nextImport.sourceKind,
      });
      setImportThreshold(IMPORT_THRESHOLD);
      setImportInvert(false);
      dispatch({ type: 'RESET_OVERLAY_TRANSFORM' });
    } catch (error) {
      console.error('Nonogram image import failed', {
        fileName: file.name,
        error,
      });
    } finally {
      event.target.value = '';
      setIsImporting(false);
    }
  }, []);

  const importPreviewLuminance = useMemo(() => {
    if (!pendingImport) return null;
    const placement = getOverlayPlacement(
      pendingImport.sourceImage,
      rows,
      cols,
      overlayScale,
      overlayOffsetX,
      overlayOffsetY,
    );
    const { width, height } = getSourceDimensions(pendingImport.sourceImage);
    const pixelScale = getImportScale(width, height, rows, cols);
    return pendingImport.sourceKind === 'bitmap'
      ? directImageToLuminanceGrid(
          pendingImport.sourceImage,
          rows,
          cols,
          placement,
        )
      : pixelateImageToLuminanceGrid(
          pendingImport.sourceImage,
          rows,
          cols,
          pixelScale,
          placement,
        );
  }, [
    pendingImport,
    rows,
    cols,
    overlayScale,
    overlayOffsetX,
    overlayOffsetY,
  ]);

  const importPreviewGrid = useMemo(() => {
    if (!importPreviewLuminance) return null;
    return gridFromLuminanceGrid(importPreviewLuminance, importThreshold, importInvert);
  }, [importPreviewLuminance, importThreshold, importInvert]);

  const handleApplyImport = useCallback(() => {
    if (!pendingImport || !importPreviewGrid) return;
    const placement = getOverlayPlacement(
      pendingImport.sourceImage,
      rows,
      cols,
      overlayScale,
      overlayOffsetX,
      overlayOffsetY,
    );
    dispatch({
      type: 'APPLY_IMAGE_IMPORT',
      grid: importPreviewGrid,
      layer: {
        image: pendingImport.overlayImage,
        placement,
      },
    });
    setPendingImport(null);
    setImportThreshold(IMPORT_THRESHOLD);
    setImportInvert(false);
  }, [cols, importPreviewGrid, overlayOffsetX, overlayOffsetY, overlayScale, pendingImport, rows]);

  const handleCancelImport = useCallback(() => {
    setPendingImport(null);
    setImportThreshold(IMPORT_THRESHOLD);
    setImportInvert(false);
  }, []);

  const handleOverlayScaleChange = useCallback((scale: number) => {
    dispatch({ type: 'SET_OVERLAY_SCALE', scale });
  }, []);

  const handleOverlayOffsetChange = useCallback((nextOffsetX: number, nextOffsetY: number) => {
    dispatch({ type: 'SET_OVERLAY_OFFSET', offsetX: nextOffsetX, offsetY: nextOffsetY });
  }, []);

  /* ---------- Region callbacks (multi grid mode) ---------- */

  const handleRegionCreate = useCallback(
    (bounds: { row: number; col: number; width: number; height: number }) => {
      if (bounds.width < 3 || bounds.height < 3) return;
      if (bounds.row < 0 || bounds.col < 0 || bounds.row + bounds.height > rows || bounds.col + bounds.width > cols) return;
      if (regionsOverlap(regions, bounds)) return;
      dispatch({
        type: 'ADD_REGION',
        region: {
          id: `${bounds.row}_${bounds.col}`,
          row: bounds.row, col: bounds.col,
          width: bounds.width, height: bounds.height,
          difficulty: 0,
        },
      });
    },
    [regions, rows, cols],
  );

  const handleRegionResize = useCallback(
    (index: number, bounds: { row: number; col: number; width: number; height: number }) => {
      if (bounds.width < 3 || bounds.height < 3) return;
      if (bounds.row < 0 || bounds.col < 0 || bounds.row + bounds.height > rows || bounds.col + bounds.width > cols) return;
      if (regionsOverlap(regions, bounds, index)) return;
      const newRegions = regions.map((r, i) =>
        i === index
          ? { ...r, id: `${bounds.row}_${bounds.col}`, row: bounds.row, col: bounds.col, width: bounds.width, height: bounds.height }
          : r,
      );
      dispatch({ type: 'SET_REGIONS', regions: newRegions.sort((a, b) => a.row - b.row || a.col - b.col) });
    },
    [regions, rows, cols],
  );

  const handleRegionDelete = useCallback(
    (index: number) => dispatch({ type: 'DELETE_REGION', index }),
    [],
  );

  const handleRegionSelect = useCallback(
    (index: number | null) => dispatch({ type: 'SET_SELECTED_REGION', index }),
    [],
  );

  /* ---------- Multi puzzle regions ---------- */

  // regionAnalysis is indexed to `effectiveRegions` (which equals `regions`
  // when the user has drawn any, otherwise a synthetic full-grid region).
  // `safeRegionAnalysis` pads missing entries so renders remain consistent
  // while a worker response is in flight.
  const safeRegionAnalysis = useMemo<RegionAnalysis[]>(
    () =>
      effectiveRegions.map(
        (r, i) => regionAnalysis[i] ?? makeEmptyRegionAnalysis(r),
      ),
    [effectiveRegions, regionAnalysis],
  );

  // Union line-logic ambiguous cells from every analyzed region.
  const ambiguousCells = useMemo(() => {
    const set = new Set<string>();
    for (const region of safeRegionAnalysis) {
      for (const key of region.ambiguousCells) set.add(key);
    }
    return set;
  }, [safeRegionAnalysis]);

  // Overall hint is shown only in "simple mode" (at most one user-drawn
  // region). In multi-region mode, per-region hints overlay the canvas.
  const overallUniquenessHint = useMemo(
    () =>
      regions.length <= 1
        ? safeRegionAnalysis[0]?.uniquenessHint ?? null
        : null,
    [regions.length, safeRegionAnalysis],
  );

  const buildExportData = useCallback(() => ({
    id: initialConfig?.kind === 'remix' ? (initialConfig.level.id ?? storageKey) : storageKey,
    name,
    rows,
    cols,
    solution: grid.map((row) => row.map((c) => (c ? '1' : '0')).join('')),
    puzzles: regions.map((r, i) => ({
      id: r.id,
      row: r.row,
      col: r.col,
      width: r.width,
      height: r.height,
      difficulty: safeRegionAnalysis[i]?.difficulty ?? r.difficulty,
    })),
  }), [name, rows, cols, grid, regions, safeRegionAnalysis, initialConfig, storageKey]);

  const handleDownloadJson = useCallback(() => {
    const blob = new Blob([JSON.stringify(buildExportData(), null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${name || 'nonogram'}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  }, [buildExportData, name]);

  const handleCopyJson = useCallback(async () => {
    await navigator.clipboard.writeText(JSON.stringify(buildExportData(), null, 2));
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 1500);
  }, [buildExportData]);

  const nonUniqueRegions = useMemo(() => {
    return safeRegionAnalysis
      .map((r, i) => ({ index: i, analysis: r }))
      .filter(({ analysis }) => !analysis.empty && analysis.unique === false);
  }, [safeRegionAnalysis]);

  const backtrackingRegions = useMemo(() => {
    return safeRegionAnalysis
      .map((r, i) => ({ index: i, analysis: r }))
      .filter(({ analysis }) => !analysis.empty && analysis.requiresBacktracking);
  }, [safeRegionAnalysis]);

  const isEmptyGrid = useMemo(
    () => !grid.some((row) => row.some(Boolean)),
    [grid],
  );

  const isExportDisabled = isEmptyGrid || nonUniqueRegions.length > 0;

  const avgDifficulty = useMemo(() => {
    const nonEmpty = safeRegionAnalysis.filter((r) => !r.empty);
    if (nonEmpty.length === 0) return 0;
    return Math.round(nonEmpty.reduce((sum, r) => sum + r.difficulty, 0) / nonEmpty.length);
  }, [safeRegionAnalysis]);

  const handleClearRegions = useCallback(() => {
    dispatch({ type: 'SET_REGIONS', regions: [] });
  }, []);

  const hasSingleFullRegion =
    regions.length === 1 &&
    regions[0].row === 0 &&
    regions[0].col === 0 &&
    regions[0].width === cols &&
    regions[0].height === rows;

  const handleSingleRegion = useCallback(() => {
    dispatch({ type: 'SET_REGIONS', regions: [makeFullGridRegion(rows, cols)] });
  }, [rows, cols]);

  const handleSplitRegions = useCallback((regionSize: number) => {
    if (rows < regionSize || cols < regionSize) return;
    if (rows % regionSize !== 0 || cols % regionSize !== 0) return;
    const newRegions: PuzzleDefinition[] = [];
    for (let r = 0; r < rows; r += regionSize) {
      for (let c = 0; c < cols; c += regionSize) {
        newRegions.push({
          id: `${r}_${c}`,
          row: r,
          col: c,
          width: regionSize,
          height: regionSize,
          difficulty: 0,
        });
      }
    }
    dispatch({ type: 'SET_REGIONS', regions: newRegions });
  }, [rows, cols]);

  const canShowSplit = useCallback((regionSize: number) => {
    if (rows < regionSize || cols < regionSize) return false;
    if (rows % regionSize !== 0 || cols % regionSize !== 0) return false;
    return (rows / regionSize) * (cols / regionSize) > 1;
  }, [rows, cols]);

  const canSplit5 = canShowSplit(5);
  const canSplit10 = canShowSplit(10);
  const canSplit15 = canShowSplit(15);

  const handleMakeUnique = useCallback(() => {
    const w = workerRef.current;
    if (!w || makeUniqueWorking) return;
    const id = ++makeUniqueIdRef.current;
    setMakeUniqueWorking(true);
    const req: AnalyzeRequest = {
      kind: 'makeUnique',
      id,
      grid,
      regions,
    };
    w.postMessage(req);
  }, [grid, regions, makeUniqueWorking]);

  const handleRemoveBacktracking = useCallback(() => {
    const w = workerRef.current;
    if (!w || removeBtWorking) return;
    const id = ++removeBtIdRef.current;
    setRemoveBtWorking(true);
    const req: AnalyzeRequest = {
      kind: 'removeBacktracking',
      id,
      grid,
      regions,
    };
    w.postMessage(req);
  }, [grid, regions, removeBtWorking]);
  const pendingOverlayPlacement = useMemo(
    () => pendingImport
      ? getOverlayPlacement(
          pendingImport.sourceImage,
          rows,
          cols,
          overlayScale,
          overlayOffsetX,
          overlayOffsetY,
        )
      : null,
    [cols, overlayOffsetX, overlayOffsetY, overlayScale, pendingImport, rows],
  );
  const canvasOverlayInteractive = mode === 'image' && pendingImport !== null;
  const canvasOverlayVisible = overlayLayers.length > 0 || pendingImport !== null;

  // Coverage: whether all cells are covered by regions
  const fullCoverage = useMemo(() => {
    const covered = new Set<string>();
    for (const region of regions) {
      for (let r = region.row; r < region.row + region.height; r++) {
        for (let c = region.col; c < region.col + region.width; c++) {
          covered.add(`${r},${c}`);
        }
      }
      }
    return covered.size === rows * cols;
  }, [regions, rows, cols]);

  const boardMessage = useMemo(() => {
    if (pendingImport) {
      if (mode === 'image') {
        return 'Pending image import: drag to frame, scroll to zoom, then apply or cancel.';
      }
      return 'Pending image import is shown on the board. Switch to Image to apply or cancel.';
    }

    if (mode === 'image') {
      if (overlayLayers.length > 0 || pendingImport?.overlayImage) {
        return 'Reference images stay fixed on the background. Import a new image to add another layer.';
      }
      return 'Import an image to preview it on the grid.';
    }

    if (mode === 'draw') {
      if (activeTool === 'spray') {
        return 'Spray: drag to scatter random fill in a 3x3 area.';
      }
      return 'Brush: click or drag to paint. Click a filled cell to erase.';
    }

    if (mode === 'regions') {
      return selectedRegion != null
        ? 'Arrange: drag region corners to resize. Use the toolbar to split or reset coverage.'
        : 'Arrange: drag on the grid to create regions.';
    }

    if (mode === 'size') {
      return 'Adjust rows and columns for the puzzle board.';
    }

    return '';
  }, [activeTool, mode, overlayLayers.length, pendingImport, selectedRegion]);

  /* ---------- Render ---------- */

  return (
    <ScreenLayout>
      <div className="creator">
        <div className="cr-title-bar">
          {isRenamingTitle ? (
            <input
              ref={titleInputRef}
              className="cr-title-bar__input"
              type="text"
              value={titleDraft}
              autoFocus
              maxLength={48}
              placeholder="Custom level"
              onChange={(e) => setTitleDraft(e.target.value)}
              onBlur={commitRenamingTitle}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commitRenamingTitle();
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  cancelRenamingTitle();
                }
              }}
              aria-label="Level name"
            />
          ) : (
            <button
              type="button"
              className="cr-title-bar__display"
              onClick={startRenamingTitle}
              aria-label={`Rename level (current name: ${displayedTitle})`}
            >
              <span className="cr-title-bar__name">{displayedTitle}</span>
              <svg
                className="cr-title-bar__icon"
                width="16"
                height="16"
                viewBox="0 0 20 20"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M14.5 3L17 5.5L7 15.5L3.5 16.5L4.5 13L14.5 3Z"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          )}
        </div>

        {/* === Main layout === */}
        <div className="cr-layout">
          <div className="cr-canvas-area">
            <div className="game-board-area" style={{ '--game-board-width': `${boardW}px` } as React.CSSProperties}>
              <div className="cr-message-strip" aria-live="polite">
                <span className="cr-message-strip__text">
                  {boardMessage || '\u00A0'}
                </span>
              </div>
              <div className="game-toolbar game-toolbar--top game-toolbar--board-aligned cr-toolbar-top">
                <div className="game-toolbar__left" />
                <div className="game-toolbar__center cr-mode-controls">
                  {mode === 'draw' && (
                    <div className="cr-draw-toolbar">
                      <PillToggle
                        options={[
                          { value: 'brush' as const, label: 'Brush' },
                          { value: 'spray' as const, label: 'Spray' },
                        ]}
                        value={activeTool}
                        onChange={(tool) => dispatch({ type: 'SET_TOOL', tool })}
                        aria-label="Draw tools"
                      />
                      {(() => {
                        const isWorking = makeUniqueWorking;
                        const isAnalyzing = analyzing && !isWorking;
                        const isOk =
                          !isWorking &&
                          !isAnalyzing &&
                          nonUniqueRegions.length === 0;
                        const label = isWorking
                          ? 'Solving…'
                          : isAnalyzing
                            ? 'Analyzing…'
                            : isOk
                              ? 'All unique'
                              : 'Make unique';
                        return (
                          <PillButton
                            onClick={handleMakeUnique}
                            disabled={isWorking || isAnalyzing || isOk}
                            className={isOk ? 'ui-pill-button--success' : undefined}
                            icon={
                              isWorking || isAnalyzing ? (
                                <span className="ui-pill-spinner" />
                              ) : isOk ? (
                                <span className="ui-pill-check" aria-hidden="true">
                                  ✓
                                </span>
                              ) : undefined
                            }
                          >
                            {label}
                          </PillButton>
                        );
                      })()}
                      {(() => {
                        const isWorking = removeBtWorking;
                        const isAnalyzing = analyzing && !isWorking;
                        const isOk =
                          !isWorking &&
                          !isAnalyzing &&
                          backtrackingRegions.length === 0;
                        const label = isWorking
                          ? 'Solving…'
                          : isAnalyzing
                            ? 'Analyzing…'
                            : isOk
                              ? 'No backtracking'
                              : 'Remove backtracking';
                        return (
                          <PillButton
                            onClick={handleRemoveBacktracking}
                            disabled={isWorking || isAnalyzing || isOk}
                            className={isOk ? 'ui-pill-button--success' : undefined}
                            icon={
                              isWorking || isAnalyzing ? (
                                <span className="ui-pill-spinner" />
                              ) : isOk ? (
                                <span className="ui-pill-check" aria-hidden="true">
                                  ✓
                                </span>
                              ) : undefined
                            }
                          >
                            {label}
                          </PillButton>
                        );
                      })()}
                    </div>
                  )}
                  {mode === 'image' && (
                    <div className="cr-image-toolbar">
                      <input
                        ref={fileInputRef}
                        className="cr-file-input"
                        type="file"
                        accept="image/*,.heic,.heif,.HEIC,.HEIF"
                        onChange={handleImportImage}
                      />
                      {pendingImport ? (
                        <PillToggle
                          options={[
                            { value: 'apply' as const, label: 'Apply image' },
                            { value: 'cancel' as const, label: 'Cancel' },
                          ]}
                          value={'__none__' as string}
                          onChange={(v) => v === 'apply' ? handleApplyImport() : handleCancelImport()}
                          aria-label="Pending image actions"
                        />
                      ) : (
                        <PillButton
                          onClick={() => fileInputRef.current?.click()}
                          disabled={isImporting}
                        >
                          {isImporting ? 'Preparing preview...' : 'Import image'}
                        </PillButton>
                      )}
                      <PillSlider
                        label="Threshold"
                        min={0}
                        max={255}
                        value={importThreshold}
                        onChange={setImportThreshold}
                        disabled={!pendingImport}
                        compact
                        aria-label="Import threshold"
                      />
                      <PillButton
                        isActive={importInvert}
                        onClick={() => setImportInvert((value) => !value)}
                        aria-pressed={importInvert}
                        disabled={!pendingImport}
                      >
                        Invert
                      </PillButton>
                    </div>
                  )}
                  {mode === 'size' && (() => {
                    const smaller = Math.max(5, Math.floor(Math.min(rows, cols) / 5) * 5 - 5);
                    const larger = Math.min(50, Math.ceil(Math.max(rows, cols) / 5) * 5 + 5);
                    return (
                      <div className="cr-size-toolbar">
                        <PillStepper
                          label="Rows"
                          min={5}
                          max={50}
                          value={rows}
                          onChange={(v) => dispatch({ type: 'RESIZE', rows: v, cols })}
                          aria-label="Rows"
                        />
                        <PillStepper
                          label="Cols"
                          min={5}
                          max={50}
                          value={cols}
                          onChange={(v) => dispatch({ type: 'RESIZE', rows, cols: v })}
                          aria-label="Cols"
                        />
                        <PillButton
                          disabled={smaller === Math.min(rows, cols) && rows === cols}
                          onClick={() => dispatch({ type: 'RESIZE', rows: smaller, cols: smaller })}
                        >
                          {smaller}×{smaller}
                        </PillButton>
                        <PillButton
                          disabled={larger === Math.max(rows, cols) && rows === cols}
                          onClick={() => dispatch({ type: 'RESIZE', rows: larger, cols: larger })}
                        >
                          {larger}×{larger}
                        </PillButton>
                      </div>
                    );
                  })()}
                  {mode === 'regions' && (
                    <div className="cr-arrange-toolbar">
                      <PillButton
                        variant="danger"
                        onClick={handleClearRegions}
                        disabled={regions.length === 0}
                      >
                        Remove all
                      </PillButton>
                      <PillButton
                        onClick={handleSingleRegion}
                        disabled={hasSingleFullRegion}
                      >
                        Single puzzle
                      </PillButton>
                      <PillButton
                        onClick={() => handleSplitRegions(5)}
                        disabled={!canSplit5}
                        aria-label={canSplit5 ? 'Split into 5x5 regions' : 'Grid must be divisible by 5'}
                      >
                        Split 5x5
                      </PillButton>
                      {canSplit10 && (
                        <PillButton
                          onClick={() => handleSplitRegions(10)}
                          aria-label="Split into 10x10 regions"
                        >
                          Split 10x10
                        </PillButton>
                      )}
                      {canSplit15 && (
                        <PillButton
                          onClick={() => handleSplitRegions(15)}
                          aria-label="Split into 15x15 regions"
                        >
                          Split 15x15
                        </PillButton>
                      )}
                    </div>
                  )}
                </div>
                <div className="game-toolbar__right" />
              </div>
              <CreatorCanvas
                grid={grid}
                rows={rows}
                cols={cols}
                mode={mode}
                overlayLayers={overlayLayers}
                pendingOverlayImage={pendingImport?.overlayImage ?? null}
                pendingOverlayPlacement={pendingOverlayPlacement}
                overlayOpacity={overlayOpacity}
                overlayVisible={canvasOverlayVisible && showOverlayImage}
                overlayScale={overlayScale}
                overlayOffsetX={overlayOffsetX}
                overlayOffsetY={overlayOffsetY}
                overlayInteractive={canvasOverlayInteractive}
                ambiguousCells={ambiguousCells}
                uniquenessHint={overallUniquenessHint}
                pendingImportGrid={importPreviewGrid}
                regions={regions}
                regionDifficulties={regions.map(
                  (_, i) => safeRegionAnalysis[i]?.difficulty ?? 0,
                )}
                regionUniqueness={regions.map(
                  (_, i) => safeRegionAnalysis[i]?.unique ?? null,
                )}
                regionUniquenessHints={regions.map(
                  (_, i) => safeRegionAnalysis[i]?.uniquenessHint ?? null,
                )}
                selectedRegion={selectedRegion}
                previewCells={previewCells}
                previewMode={previewMode}
                onCellDown={handleCellDown}
                onCellMove={handleCellMove}
                onCellUp={handleCellUp}
                onCellLeave={handleCellLeave}
                onOverlayOffsetChange={handleOverlayOffsetChange}
                onOverlayScaleChange={handleOverlayScaleChange}
                onRegionCreate={handleRegionCreate}
                onRegionResize={handleRegionResize}
                onRegionDelete={handleRegionDelete}
                onRegionSelect={handleRegionSelect}
                showClues={showClues}
                showSpanHints={showSpanHints}
              />
              <div className="game-toolbar game-toolbar--bottom game-toolbar--board-aligned">
                <div className="game-toolbar__left">
                  <SegmentedToggle
                    className="cr-mode-toggle"
                    label="Editor mode"
                    options={[
                      { value: 'size' as const, label: 'Size' },
                      ...(multiPuzzleEnabled
                        ? [{ value: 'regions' as const, label: 'Arrange' }]
                        : []),
                      { value: 'draw' as const, label: 'Draw' },
                      { value: 'image' as const, label: 'Image' },
                    ]}
                    value={mode}
                    onChange={(v) => setMode(v)}
                  />
                </div>
                <div className="game-toolbar__center" />
                <div className="game-toolbar__right">
                  <div className="game-footer-cluster game-footer-cluster--history" role="group" aria-label="History controls">
                    <button
                      className="game-history-btn"
                      onClick={() => dispatch({ type: 'UNDO' })}
                      aria-label="Undo"
                      disabled={state.history.length === 0}
                    >
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                        <path d="M4 8L8 4M4 8L8 12M4 8H13C14.6569 8 16 9.3431 16 11V12C16 13.6569 14.6569 15 13 15H10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                    <button
                      className="game-history-btn"
                      onClick={() => dispatch({ type: 'REDO' })}
                      aria-label="Redo"
                      disabled={state.future.length === 0}
                    >
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                        <path d="M16 8L12 4M16 8L12 12M16 8H7C5.34315 8 4 9.3431 4 11V12C4 13.6569 5.34315 15 7 15H10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                    <button
                      className="game-history-btn game-history-btn--clear"
                      onClick={handleClearBoard}
                      aria-label="Clear board"
                    >
                      <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
                        <path d="M3 5H15M6 5V4C6 3.44772 6.44772 3 7 3H11C11.5523 3 12 3.44772 12 4V5M7 8V13M11 8V13M4 5L5 14C5 14.5523 5.44772 15 6 15H12C12.5523 15 13 14.5523 13 14L14 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>

        <div className="cr-view-toggles">
          <SettingsToggleRow
            className="cr-clue-display-row"
            label="Display clues"
            checked={showClues}
            onChange={setShowClues}
          />
          <div className="cr-primary-actions">
            <Button
              variant="ghost"
              size="wide-sm"
              className={`cr-uniqueness-btn${
                analyzing
                  ? ''
                  : nonUniqueRegions.length > 0
                    ? ' cr-uniqueness-btn--warn'
                    : ' cr-uniqueness-btn--ok'
              }`}
              onClick={() => setShowUniquenessPopup(true)}
              disabled={analyzing}
            >
              {analyzing
                ? 'Analyzing…'
                : nonUniqueRegions.length > 0
                  ? 'Not unique'
                  : 'Unique'}
            </Button>
            <Button
              variant="ghost"
              size="wide-sm"
              className="cr-difficulty-btn"
              onClick={() => setShowDifficultyPopup(true)}
              disabled={analyzing}
            >
              {analyzing ? 'D…' : `D${avgDifficulty}`}
            </Button>
            <Button
              variant="ghost"
              size="wide-sm"
              className="cr-play-btn"
              onClick={handlePlay}
              disabled={isExportDisabled}
            >
              Play
            </Button>
            <Button variant="ghost" size="custom" className="cr-icon-btn" onClick={handleShare} aria-label="Share puzzle" disabled={isExportDisabled}>
              <svg width="20" height="20" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <circle cx="12" cy="3" r="2" stroke="currentColor" strokeWidth="1.4" />
                <circle cx="4" cy="8" r="2" stroke="currentColor" strokeWidth="1.4" />
                <circle cx="12" cy="13" r="2" stroke="currentColor" strokeWidth="1.4" />
                <path d="M5.8 7L10.2 4M5.8 9L10.2 12" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
              </svg>
            </Button>
            <Button variant="ghost" size="custom" className="cr-icon-btn" onClick={handleDownloadJson} aria-label="Download as JSON" disabled={isExportDisabled}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path d="M10 3V13M10 13L6 9M10 13L14 9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M4 15V16C4 16.5523 4.44772 17 5 17H15C15.5523 17 16 16.5523 16 16V15" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
              </svg>
            </Button>
            <Button variant="ghost" size="custom" className="cr-icon-btn" onClick={handleCopyJson} aria-label="Copy JSON" disabled={isExportDisabled}>
              {copySuccess ? (
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                  <path d="M4 10L8 14L16 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                  <rect x="7" y="7" width="9" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
                  <path d="M13 7V5C13 4.44772 12.5523 4 12 4H5C4.44772 4 4 4.44772 4 5V13C4 13.5523 4.44772 14 5 14H7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                </svg>
              )}
            </Button>
          </div>
          {overlayLayers.length > 0 && (
            <PillButton
              isActive={showOverlayImage}
              onClick={() => setShowOverlayImage((v) => !v)}
              aria-pressed={showOverlayImage}
            >
              Background image{overlayLayers.length > 1 ? 's' : ''}
            </PillButton>
          )}
        </div>

        {/* Uniqueness popup */}
        {showUniquenessPopup && (
          <Popup onDismiss={() => setShowUniquenessPopup(false)} maxWidth={340} aria-label="Uniqueness">
            <PopupHeader title="Uniqueness" onClose={() => setShowUniquenessPopup(false)} />
            <PopupBody>
              <ul className="cr-info-popup-list">
                {safeRegionAnalysis.map((r, i) => (
                  <li key={i}>
                    Region {i + 1}
                    <span className="cr-info-popup-detail">
                      {r.empty ? '—' : r.unique === false
                        ? <span className="cr-info-popup-warn">not unique</span>
                        : r.unique === true
                          ? <span className="cr-info-popup-ok">unique</span>
                          : '…'}
                    </span>
                  </li>
                ))}
              </ul>
            </PopupBody>
          </Popup>
        )}

        {/* Difficulty popup */}
        {showDifficultyPopup && (
          <Popup onDismiss={() => setShowDifficultyPopup(false)} maxWidth={340} aria-label="Difficulty per region">
            <PopupHeader title="Difficulty" onClose={() => setShowDifficultyPopup(false)} />
            <PopupBody>
              <ul className="cr-info-popup-list">
                {safeRegionAnalysis.map((r, i) => (
                  <li key={i}>
                    Region {i + 1}
                    <span className="cr-info-popup-detail">
                      {r.empty ? '—' : `D${r.difficulty}`}
                      {!r.empty && r.unique === false && <span className="cr-info-popup-warn"> · not unique</span>}
                    </span>
                  </li>
                ))}
              </ul>
              <div className="cr-info-popup-avg">Average: D{avgDifficulty}</div>
            </PopupBody>
          </Popup>
        )}

        {/* Share popup */}
        {showSharePopup && (
          <SharePopup
            hasProgress={false}
            onShare={async () => {
              try {
                const shareUrl = await ShareService.url({ solution: grid });
                const result = await ShareService.shareOrCopy(shareUrl);
                if (result === 'copied') {
                  setToast('Link copied!');
                  setTimeout(() => setToast(null), 1500);
                } else if (result === 'shared') {
                  setToast('Shared!');
                  setTimeout(() => setToast(null), 1500);
                }
                return result;
              } catch {
                return 'failed';
              }
            }}
            onClose={() => setShowSharePopup(false)}
          />
        )}

        {toast && (
          <div className="cr-toast" aria-live="polite">
            {toast}
          </div>
        )}
      </div>
    </ScreenLayout>
  );
}

export default Creator;
